import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { GiftIcon as Gift, PlusIcon as Plus, TrashIcon as Trash, PencilSimpleIcon as PencilSimple, TagIcon as Tag, ToggleLeftIcon as ToggleLeft, ToggleRightIcon as ToggleRight } from "@phosphor-icons/react";

/**
 * Admin Comp List console.
 *
 * Single-purpose page for managing per-email courtesy discounts:
 *   - "free" : 100% off every paid surface for that email
 *   - "percent" : N% off every paid surface
 *   - "promo_link" : auto-apply this existing promo code at every checkout
 *
 * Lives at /admin/comp-list. Linked from AdminConsole. Operator
 * mental model: "I gave $email a courtesy." We key on email rather
 * than user_id so the comp survives account-merge / email-link edge
 * cases.
 */
export default function AdminCompList() {
  const [rows, setRows] = useState([]);
  const [promos, setPromos] = useState([]);
  const [totals, setTotals] = useState([]);
  const [history, setHistory] = useState({ email: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    email: "",
    kind: "free",
    value: 100,
    promo_code_id: "",
    note: "",
    active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [compRes, promoRes, totalsRes] = await Promise.all([
        api.get("/admin/comp-list"),
        api.get("/admin/promo-codes").catch(() => ({ data: [] })),
        api.get("/admin/comp-list/totals").catch(() => ({ data: [] })),
      ]);
      setRows(compRes.data || []);
      setPromos(promoRes.data || []);
      setTotals(totalsRes.data || []);
    } catch (e) {
      toast.error("Could not load comp list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => setForm({
    email: "",
    kind: "free",
    value: 100,
    promo_code_id: "",
    note: "",
    active: true,
  });

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      email: row.email,
      kind: row.kind,
      value: row.value ?? 100,
      promo_code_id: row.promo_code_id || "",
      note: row.note || "",
      active: row.active,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        kind: form.kind,
        note: form.note || "",
        active: form.active,
      };
      if (form.kind === "percent") {
        payload.value = Number(form.value);
      }
      if (form.kind === "promo_link") {
        payload.promo_code_id = form.promo_code_id;
      }
      if (editing) {
        // PATCH only the fields that may have changed.
        const update = {
          kind: payload.kind,
          note: payload.note,
          active: payload.active,
        };
        if (payload.kind === "percent") update.value = payload.value;
        if (payload.kind === "promo_link") update.promo_code_id = payload.promo_code_id;
        await api.patch(`/admin/comp-list/${editing.id}`, update);
        toast.success("Comp updated");
      } else {
        await api.post("/admin/comp-list", payload);
        toast.success("Comp added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove comp for ${row.email}?`)) return;
    try {
      await api.delete(`/admin/comp-list/${row.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/comp-list/${row.id}`, { active: !row.active });
      load();
    } catch (e) {
      toast.error("Could not toggle");
    }
  };

  const showHistory = async (row) => {
    try {
      const r = await api.get("/admin/comp-list/redemptions", { params: { email: row.email, limit: 200 } });
      setHistory({ email: row.email, rows: r.data || [] });
    } catch (e) {
      toast.error("Could not load history");
    }
  };

  const totalSaved = totals.reduce((s, t) => s + (t.total_discount || 0), 0);
  const totalUses = totals.reduce((s, t) => s + (t.uses || 0), 0);
  const totalsByEmail = totals.reduce((acc, t) => {
    acc[t.email] = t;
    return acc;
  }, {});

  const labelFor = (row) => {
    if (row.kind === "free") return "Free everywhere";
    if (row.kind === "percent") return `${row.value}% off everywhere`;
    if (row.kind === "promo_link") {
      const p = promos.find((x) => x.id === row.promo_code_id);
      return p ? `Auto-applies ${p.code}` : "Auto-applies (code not found)";
    }
    return row.kind;
  };

  return (
    <AppShell>
      <div className="max-w-5xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">Admin</p>
            <h1 className="font-spectral font-light text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Comp List
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
              Per-email courtesy discounts auto-applied at every platform-fee checkout. Use this to comp yourself, co-owners, partners, or anyone you've extended a no-charge / discounted relationship to. The user sees a tasteful "Comp on file" banner at checkout — no code to memorize.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="comp-add-btn"
          >
            <Plus size={14} weight="bold" className="mr-1.5" /> Add comp
          </Button>
        </div>

        <div className="gold-hairline my-6 max-w-md" />

        {/* Savings summary strip — collapses gracefully when no
            redemptions are on file yet. Drives the "useful for tax /
            accounting" use case mentioned 2026-02-27. */}
        {totals.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="comp-totals-strip">
            <div className="rounded-xl border border-[#EAEAEA] bg-white p-4 shadow-luxury-light">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Total saved (all-time)</p>
              <p className="font-spectral text-3xl mt-1 text-ink">${totalSaved.toFixed(2)}</p>
              <p className="text-[11px] text-[#6B6B6B] mt-0.5">Across {totals.length} comp recipient{totals.length === 1 ? "" : "s"}</p>
            </div>
            <div className="rounded-xl border border-[#EAEAEA] bg-white p-4 shadow-luxury-light">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Redemptions</p>
              <p className="font-spectral text-3xl mt-1 text-ink">{totalUses}</p>
              <p className="text-[11px] text-[#6B6B6B] mt-0.5">Free-unlocks + paid-with-discount</p>
            </div>
            <div className="rounded-xl border border-[#EAEAEA] bg-white p-4 shadow-luxury-light">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Top recipient</p>
              <p className="font-spectral text-lg mt-1 text-ink truncate" title={totals[0]?.email}>{totals[0]?.email || "—"}</p>
              <p className="text-[11px] text-[#6B6B6B] mt-0.5">${(totals[0]?.total_discount || 0).toFixed(2)} saved · {totals[0]?.uses || 0} uses</p>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-[#6B6B6B]">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center" data-testid="comp-empty">
            <Gift size={32} weight="duotone" className="text-[#C4C4C4] mx-auto mb-3" />
            <p className="text-sm text-ink font-semibold">No comp entries yet</p>
            <p className="text-xs text-[#6B6B6B] mt-1">
              Add your own email or a co-owner's to auto-apply a discount everywhere they check out.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden shadow-luxury-light" data-testid="comp-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#EAEAEA]">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Comp</th>
                  <th className="px-4 py-3 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Note</th>
                  <th className="px-4 py-3 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Saved</th>
                  <th className="px-4 py-3 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Active</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA]/50" data-testid={`comp-row-${row.id}`}>
                    <td className="px-4 py-3 font-mono text-[12.5px] text-ink">{row.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#8A6A00] bg-cream rounded-full px-2 py-0.5">
                        {row.kind === "free" ? <Gift size={11} weight="fill" /> : <Tag size={11} weight="fill" />}
                        {labelFor(row)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12.5px] text-[#6B6B6B] max-w-[260px] truncate" title={row.note}>{row.note || "—"}</td>
                    <td className="px-4 py-3 text-[12.5px]">
                      {totalsByEmail[row.email] ? (
                        <button
                          type="button"
                          onClick={() => showHistory(row)}
                          className="text-left hover:underline"
                          data-testid={`comp-history-${row.id}`}
                          title="Click to see redemption history"
                        >
                          <span className="font-semibold text-ink font-tabular">${totalsByEmail[row.email].total_discount.toFixed(2)}</span>
                          <span className="text-[#6B6B6B] ml-1">· {totalsByEmail[row.email].uses} uses</span>
                        </button>
                      ) : (
                        <span className="text-[#C4C4C4]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(row)}
                        className="inline-flex items-center gap-1 text-[12px] font-semibold"
                        data-testid={`comp-toggle-${row.id}`}
                        aria-label={row.active ? "Deactivate" : "Activate"}
                      >
                        {row.active ? (
                          <ToggleRight size={22} weight="fill" className="text-[#0F8043]" />
                        ) : (
                          <ToggleLeft size={22} weight="duotone" className="text-[#C4C4C4]" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => openEdit(row)}
                          data-testid={`comp-edit-${row.id}`}
                        >
                          <PencilSimple size={13} weight="duotone" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-crimson hover:bg-[#FFF0F0]"
                          onClick={() => remove(row)}
                          data-testid={`comp-delete-${row.id}`}
                        >
                          <Trash size={13} weight="duotone" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="comp-dialog">
          <DialogHeader>
            <DialogTitle className="font-spectral text-2xl">
              {editing ? "Edit comp" : "Add comp"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-1.5 block">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="co-owner@example.com"
                disabled={!!editing}
                data-testid="comp-email-input"
              />
              {editing && <p className="text-[11px] text-[#6B6B6B] mt-1">Email can't be changed. Delete and re-add to change the email.</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-1.5 block">Comp type</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="comp-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free everywhere (100% off)</SelectItem>
                  <SelectItem value="percent">Percent off</SelectItem>
                  <SelectItem value="promo_link">Auto-apply existing promo code</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.kind === "percent" && (
              <div>
                <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-1.5 block">Percent (1–100)</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  data-testid="comp-value-input"
                />
              </div>
            )}
            {form.kind === "promo_link" && (
              <div>
                <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-1.5 block">Linked promo code</label>
                <Select value={form.promo_code_id} onValueChange={(v) => setForm({ ...form, promo_code_id: v })}>
                  <SelectTrigger data-testid="comp-promo-select"><SelectValue placeholder="Pick a promo…" /></SelectTrigger>
                  <SelectContent>
                    {promos.length === 0 ? (
                      <div className="px-3 py-2 text-[12px] text-[#6B6B6B]">No promo codes yet — create one at /admin/promo-codes first.</div>
                    ) : promos.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.code} · {p.kind === "percent" ? `${p.value}%` : `$${p.value}`} off
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-1.5 block">Note (admin-only)</label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. Co-owner of Atlas + Cleo · ICO partner"
                data-testid="comp-note-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              className="bg-ink hover:bg-crimson text-white"
              onClick={submit}
              disabled={!form.email || (form.kind === "promo_link" && !form.promo_code_id)}
              data-testid="comp-submit-btn"
            >
              {editing ? "Save changes" : "Add comp"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!history.email} onOpenChange={(v) => !v && setHistory({ email: null, rows: [] })}>
        <DialogContent className="sm:max-w-[640px]" data-testid="comp-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-spectral text-2xl">Redemption history</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#6B6B6B] mb-2 font-mono">{history.email}</p>
          {history.rows.length === 0 ? (
            <p className="text-sm text-[#6B6B6B] py-6 text-center">No redemptions yet for this email.</p>
          ) : (
            <div className="max-h-[480px] overflow-y-auto -mx-6 px-6">
              <table className="w-full text-[12.5px]">
                <thead className="border-b border-[#EAEAEA] sticky top-0 bg-white">
                  <tr className="text-left">
                    <th className="py-2 pr-3 font-semibold text-[#6B6B6B] text-[10px] uppercase tracking-wider">Date</th>
                    <th className="py-2 pr-3 font-semibold text-[#6B6B6B] text-[10px] uppercase tracking-wider">Item</th>
                    <th className="py-2 pr-3 font-semibold text-[#6B6B6B] text-[10px] uppercase tracking-wider text-right">Base</th>
                    <th className="py-2 font-semibold text-[#6B6B6B] text-[10px] uppercase tracking-wider text-right">Saved</th>
                  </tr>
                </thead>
                <tbody>
                  {history.rows.map((r) => (
                    <tr key={r.id} className="border-b border-[#F5F5F5] last:border-0">
                      <td className="py-2 pr-3 font-tabular text-[#6B6B6B]">{r.created_at?.slice(0, 10)}</td>
                      <td className="py-2 pr-3">
                        <div className="font-semibold text-ink">{r.package_id}</div>
                        <div className="text-[11px] text-[#6B6B6B]">{r.kind}</div>
                      </td>
                      <td className="py-2 pr-3 font-tabular text-right text-[#6B6B6B]">${(r.base_amount || 0).toFixed(2)}</td>
                      <td className="py-2 font-tabular text-right font-semibold text-[#0F8043]">−${(r.discount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#EAEAEA]">
                    <td colSpan={2} className="py-2 pr-3 font-semibold text-ink">Total</td>
                    <td className="py-2 pr-3 font-tabular text-right font-semibold text-[#6B6B6B]">
                      ${history.rows.reduce((s, r) => s + (r.base_amount || 0), 0).toFixed(2)}
                    </td>
                    <td className="py-2 font-tabular text-right font-bold text-[#0F8043]">
                      −${history.rows.reduce((s, r) => s + (r.discount || 0), 0).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
